import { useEffect, useRef, useState } from 'react';
import { cn } from '../../../lib/utils';
import { authenticatedFetch } from '../../../lib/api';

export type OfferCrmContact = {
  _id: string;
  customerOrganizationId?: string;
  customerOrganizationName?: string;
  recipientEmail?: string;
  recipientName?: string;
  contactName?: string;
  contactEmail?: string;
  contactPhoneCountryCode?: string;
  contactPhone?: string;
  contactDepartment?: string;
  offerCount?: number;
};

interface OfferCrmSuggestFieldProps {
  label: React.ReactNode;
  value: string;
  onChange: (value: string) => void;
  onSelectContact: (contact: OfferCrmContact) => void;
  placeholder?: string;
  inputClassName?: string;
  type?: 'text' | 'email';
  uppercase?: boolean;
  disabled?: boolean;
  hideLabel?: boolean;
  labelClassName?: string;
}

function contactSummary(c: OfferCrmContact): string {
  const parts = [
    c.customerOrganizationName,
    c.recipientEmail,
    c.contactName,
    c.contactDepartment,
  ].filter(Boolean);
  return parts.join(' · ');
}

export function OfferCrmSuggestField({
  label,
  value,
  onChange,
  onSelectContact,
  placeholder,
  inputClassName,
  type = 'text',
  uppercase = false,
  disabled = false,
  hideLabel = false,
  labelClassName = 'text-xs font-semibold text-[hsl(var(--foreground))]',
}: OfferCrmSuggestFieldProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState<OfferCrmContact[]>([]);
  const debounceRef = useRef<number | null>(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  useEffect(() => {
    const q = value.trim();
    if (!open || q.length < 1) {
      setSuggestions([]);
      return;
    }

    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(async () => {
      setLoading(true);
      try {
        const res = await authenticatedFetch(
          `/api/v1/admin/crm-contacts/suggest?search=${encodeURIComponent(q)}&limit=12`
        );
        const json = await res.json();
        if (json?.success && Array.isArray(json.data?.contacts)) {
          setSuggestions(json.data.contacts);
        } else {
          setSuggestions([]);
        }
      } catch {
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    }, 280);

    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
    };
  }, [value, open]);

  const handleChange = (raw: string) => {
    const next = uppercase ? raw.toUpperCase() : raw;
    onChange(next);
    setOpen(true);
  };

  const pick = (contact: OfferCrmContact) => {
    onSelectContact(contact);
    setOpen(false);
  };

  return (
    <div ref={rootRef} className={cn('relative space-y-1.5', open && suggestions.length > 0 && 'z-40')}>
      {!hideLabel && (
        <label className={labelClassName}>{label}</label>
      )}
      <input
        type={type}
        value={value}
        disabled={disabled}
        placeholder={placeholder}
        onChange={(e) => handleChange(e.target.value)}
        onFocus={() => setOpen(true)}
        autoComplete="off"
        className={cn(inputClassName, uppercase && 'uppercase')}
        style={uppercase ? { textTransform: 'uppercase' } : undefined}
      />
      {open && !disabled && value.trim().length >= 1 && (
        <ul className="absolute left-0 right-0 z-[70] mt-1 max-h-56 w-full overflow-y-auto rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))] shadow-lg dark:border-white/15 dark:bg-[#0f172a]">
          {loading && (
            <li className="px-3 py-2 text-sm text-[hsl(var(--muted-foreground))]">Searching CRM…</li>
          )}
          {!loading && suggestions.length === 0 && (
            <li className="px-3 py-2 text-sm text-[hsl(var(--muted-foreground))]">No CRM matches</li>
          )}
          {!loading &&
            suggestions.map((contact) => (
              <li key={contact._id}>
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => pick(contact)}
                  className="w-full px-3 py-2 text-left text-sm hover:bg-[#0088cc]/10 dark:hover:bg-[#0088cc]/20"
                >
                  <div className="font-semibold">{contactSummary(contact) || 'Saved contact'}</div>
                  {(contact.contactPhone || contact.contactEmail) && (
                    <div className="text-[10px] text-[hsl(var(--muted-foreground))]">
                      {[contact.contactEmail, contact.contactPhone].filter(Boolean).join(' · ')}
                    </div>
                  )}
                </button>
              </li>
            ))}
        </ul>
      )}
    </div>
  );
}
